/**
 * MetricsDashboard Component - Sprint 9
 * 
 * Displays upload performance metrics and network quality diagnostics
 * Features:
 * - Real-time metrics during upload
 * - Historical metrics from IndexedDB
 * - Network quality monitoring
 * - JSON export functionality
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Statistic, Row, Col, Progress, Button, Space, Tag, Divider } from 'antd';
import {
  CloudUploadOutlined,
  DashboardOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { IUploadMetrics, INetworkQualitySnapshot } from '../../types/analytics';
import {
  getAllUploadMetrics,
  getRecentNetworkQuality,
  clearAllAnalytics,
} from '../../idb/chunk-db';
import logger from '../../utils/logger';
import './MetricsDashboard.scss';

interface MetricsDashboardProps {
  videoGuid?: string;
  currentMetrics?: {
    uploadedChunks: number;
    totalChunks: number;
    avgSpeed: number;
    successRate: number;
  };
  isUploading?: boolean;
}

export const MetricsDashboard: React.FC<MetricsDashboardProps> = ({
  videoGuid,
  currentMetrics,
  isUploading = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [historicalMetrics, setHistoricalMetrics] = useState<IUploadMetrics[]>([]);
  const [networkQuality, setNetworkQuality] = useState<INetworkQualitySnapshot | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Password protection - verify query string parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const providedPassword = searchParams.get('pwd');
    const requiredPassword = process.env.REACT_APP_METRICS_DASHBOARD_PASSWORD;

    // If password is not configured, allow access (dev mode)
    if (!requiredPassword) {
      setIsAuthorized(true);
      logger.warn('Metrics dashboard password not configured - allowing access');
      return;
    }

    // Verify password
    if (providedPassword === requiredPassword) {
      setIsAuthorized(true);
      logger.info('Metrics dashboard access granted');
    } else {
      logger.warn('Metrics dashboard access denied - invalid password');
      // Redirect silently to main entry component
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  // Load historical metrics
  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const metrics = await getAllUploadMetrics();
        setHistoricalMetrics(metrics);
        logger.info(`Loaded ${metrics.length} historical metrics`);
      } catch (error) {
        logger.error('Error loading historical metrics:', error);
      }
    };

    loadMetrics();
  }, [videoGuid, isUploading]);

  // Load network quality
  useEffect(() => {
    const loadNetworkQuality = async () => {
      try {
        const snapshots = await getRecentNetworkQuality(1);
        if (snapshots.length > 0) {
          setNetworkQuality(snapshots[0]);
        }
      } catch (error) {
        logger.error('Error loading network quality:', error);
      }
    };

    loadNetworkQuality();
    
    // Refresh network quality every 10 seconds
    const interval = setInterval(loadNetworkQuality, 10000);
    return () => clearInterval(interval);
  }, []);

  // Calculate aggregate statistics from historical data
  const aggregateStats = React.useMemo(() => {
    if (historicalMetrics.length === 0) {
      return {
        avgSpeed: 0,
        avgSuccessRate: 0,
        totalUploads: 0,
        avgDuration: 0,
        avgTTFB: 0,
      };
    }

    const totalSpeed = historicalMetrics.reduce((sum, m) => sum + m.avgSpeed, 0);
    const totalSuccessRate = historicalMetrics.reduce((sum, m) => sum + m.chunkSuccessRate, 0);
    const totalDuration = historicalMetrics.reduce((sum, m) => sum + m.uploadDuration, 0);
    const validTTFB = historicalMetrics.filter(m => m.ttfb > 0);
    const totalTTFB = validTTFB.reduce((sum, m) => sum + m.ttfb, 0);

    return {
      avgSpeed: totalSpeed / historicalMetrics.length,
      avgSuccessRate: totalSuccessRate / historicalMetrics.length,
      totalUploads: historicalMetrics.length,
      avgDuration: totalDuration / historicalMetrics.length,
      avgTTFB: validTTFB.length > 0 ? totalTTFB / validTTFB.length : 0,
    };
  }, [historicalMetrics]);

  // Export metrics as JSON
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportData = {
        timestamp: new Date().toISOString(),
        totalUploads: historicalMetrics.length,
        aggregateStats,
        currentMetrics,
        networkQuality,
        uploads: historicalMetrics,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dadan-metrics-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);

      logger.info('Metrics exported successfully');
    } catch (error) {
      logger.error('Error exporting metrics:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Clear old data
  const handleClearOldData = async () => {
    try {
      await clearAllAnalytics();
      const metrics = await getAllUploadMetrics();
      setHistoricalMetrics(metrics);
      logger.info('All analytics data cleared');
    } catch (error) {
      logger.error('Error clearing analytics data:', error);
    }
  };

  // Get network quality color
  const getQualityColor = (quality: string): string => {
    switch (quality) {
      case 'excellent':
        return 'green';
      case 'good':
        return 'blue';
      case 'fair':
        return 'orange';
      case 'poor':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Format duration (ms to human readable)
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Don't render dashboard until authorized
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="metrics-dashboard">
      <div className="dashboard-header">
        <Space>
          <DashboardOutlined style={{ fontSize: '24px' }} />
          <h2>Upload Diagnostics & Analytics</h2>
        </Space>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={isExporting}
          >
            Export JSON
          </Button>
          <Button onClick={handleClearOldData} size="small">
            Clear Old Data
          </Button>
        </Space>
      </div>

      <Divider />

      {/* Current Upload Metrics */}
      {isUploading && currentMetrics && (
        <>
          <Card title="Current Upload" className="current-upload-card">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Upload Progress"
                  value={currentMetrics.uploadedChunks}
                  suffix={`/ ${currentMetrics.totalChunks}`}
                  prefix={<CloudUploadOutlined />}
                />
                <Progress
                  percent={Math.round(
                    (currentMetrics.uploadedChunks / currentMetrics.totalChunks) * 100
                  )}
                  status="active"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Upload Speed"
                  value={currentMetrics.avgSpeed}
                  suffix="Mbps"
                  precision={2}
                  prefix={<RocketOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Success Rate"
                  value={currentMetrics.successRate}
                  suffix="%"
                  precision={1}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                {networkQuality && (
                  <div>
                    <div className="network-quality-label">Network Quality</div>
                    <Tag color={getQualityColor(networkQuality.quality)} style={{ fontSize: '14px' }}>
                      {networkQuality.quality.toUpperCase()}
                    </Tag>
                    <div className="network-latency">
                      Latency: {networkQuality.latency}ms
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card>
          <Divider />
        </>
      )}

      {/* Aggregate Statistics */}
      <Card title="Historical Performance" className="aggregate-stats-card">
        <Row gutter={16}>
          <Col span={4}>
            <Statistic
              title="Total Uploads"
              value={aggregateStats.totalUploads}
              prefix={<CloudUploadOutlined />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="Avg Speed"
              value={aggregateStats.avgSpeed}
              suffix="Mbps"
              precision={2}
              prefix={<RocketOutlined />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="Avg Success Rate"
              value={aggregateStats.avgSuccessRate}
              suffix="%"
              precision={1}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="Avg Duration"
              value={formatDuration(aggregateStats.avgDuration)}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title="Avg TTFB"
              value={aggregateStats.avgTTFB > 0 ? formatDuration(aggregateStats.avgTTFB) : 'N/A'}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      <Divider />

      {/* Network Quality Details */}
      {networkQuality && (
        <Card title="Network Quality Diagnostics" className="network-quality-card">
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Quality Rating"
                value={networkQuality.quality}
                valueRender={() => (
                  <Tag color={getQualityColor(networkQuality.quality)} style={{ fontSize: '16px' }}>
                    {networkQuality.quality.toUpperCase()}
                  </Tag>
                )}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Latency"
                value={networkQuality.latency}
                suffix="ms"
                prefix={networkQuality.latency > 200 ? <WarningOutlined /> : <CheckCircleOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Bandwidth"
                value={networkQuality.bandwidth}
                suffix="Mbps"
                precision={2}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Packet Loss"
                value={networkQuality.packetLoss}
                suffix="%"
                precision={2}
                prefix={networkQuality.packetLoss > 5 ? <WarningOutlined /> : <CheckCircleOutlined />}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Recent Uploads Table */}
      {historicalMetrics.length > 0 && (
        <>
          <Divider />
          <Card title="Recent Uploads" className="recent-uploads-card">
            <div className="recent-uploads-list">
              {historicalMetrics.slice(0, 5).map((metric, index) => (
                <div key={metric.id} className="upload-item">
                  <div className="upload-item-header">
                    <span className="upload-number">#{historicalMetrics.length - index}</span>
                    <span className="upload-time">
                      {new Date(metric.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="upload-item-stats">
                    <span>Speed: {metric.avgSpeed.toFixed(2)} Mbps</span>
                    <span>Success: {metric.chunkSuccessRate.toFixed(1)}%</span>
                    <span>Duration: {formatDuration(metric.uploadDuration)}</span>
                    <span>Chunks: {metric.uploadedChunks}/{metric.totalChunks}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default MetricsDashboard;
